/**
 * Start DM Modal Component
 * Modal to select users and start a new DM (1:1 or group)
 */

import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { useUsers, useCreateDMThread } from '@/hooks/useDMs';
import { useEnabledAssistants } from '@/hooks/useAssistants';
import { useAuthStore } from '@/store/auth.store';
import { useAppStore } from '@/store/app.store';
import Avatar from '@/components/common/Avatar';
import { Bot, X, Check } from 'lucide-react';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import type { PartialUser } from '@/types/api.types';

interface StartDMModalProps {
  onClose: () => void;
}

const StartDMModal = ({ onClose }: StartDMModalProps) => {
  const { user: currentUser } = useAuthStore();
  const { setActiveDMThread } = useAppStore();
  const { data: users, isLoading, error } = useUsers();
  const { data: enabledAssistants } = useEnabledAssistants();
  const createDMThreadMutation = useCreateDMThread();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const { dialogRef, titleId } = useDialogA11y(onClose);

  // Merge regular users + assistant user accounts
  const allUsers = useMemo(() => {
    const userList: PartialUser[] = [...(users || [])];
    if (enabledAssistants) {
      const existingIds = new Set(userList.map((u) => u.id));
      for (const a of enabledAssistants) {
        const account = a.users_assistant_account;
        if (account && !existingIds.has(account.id)) {
          userList.push({ ...account, is_bot: true });
        }
      }
    }
    return userList;
  }, [users, enabledAssistants]);

  // Filter out current user and search
  const filteredUsers = allUsers
    ?.filter((user) => user.id !== currentUser?.id)
    .filter((user) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        (user.display_name || '').toLowerCase().includes(query) ||
        (user.email || '').toLowerCase().includes(query)
      );
    });

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleStartDM = async () => {
    if (selectedUserIds.length === 0) return;

    try {
      const thread = await createDMThreadMutation.mutateAsync(selectedUserIds);
      setActiveDMThread(thread.id);
      onClose();
    } catch {
      toast.error('Грешка при създаване на разговора');
    }
  };

  // Quick start: click a user directly for 1:1 DM
  const handleQuickStart = async (userId: string) => {
    if (selectedUserIds.length > 0) {
      // If already selecting, toggle instead
      toggleUser(userId);
      return;
    }
    try {
      const thread = await createDMThreadMutation.mutateAsync(userId);
      setActiveDMThread(thread.id);
      onClose();
    } catch {
      toast.error('Грешка при създаване на разговора');
    }
  };

  const selectedUsers = allUsers?.filter((u) => selectedUserIds.includes(u.id)) || [];

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border-primary">
          <div className="flex items-center justify-between">
            <h2 id={titleId} className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary">
              Ново съобщение
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Selected Users Tags */}
          {selectedUsers.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {selectedUsers.map((user) => (
                <span
                  key={user.id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium"
                >
                  {user.display_name}
                  <button
                    onClick={() => toggleUser(user.id)}
                    className="hover:text-blue-900 dark:hover:text-blue-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Search Input */}
          <div className="mt-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Търси по име или имейл..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border-secondary dark:bg-dark-bg-input dark:text-dark-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
        </div>

        {/* User List */}
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          {isLoading && (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-12 bg-gray-200 dark:bg-dark-bg-input rounded-lg animate-pulse"
                />
              ))}
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600">
              Грешка при зареждане на потребителите
            </div>
          )}

          {!isLoading && !error && (
            <>
              {!filteredUsers || filteredUsers.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-dark-text-muted text-center py-8">
                  {searchQuery
                    ? 'Няма намерени потребители'
                    : 'Няма налични потребители'}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredUsers.map((user) => {
                    const isSelected = selectedUserIds.includes(user.id);
                    return (
                      <button
                        key={user.id}
                        onClick={() => handleQuickStart(user.id)}
                        disabled={createDMThreadMutation.isPending}
                        className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-300 dark:ring-blue-700'
                            : 'hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary'
                        }`}
                      >
                        {/* Checkbox area */}
                        <div className={`flex-shrink-0 mr-2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected
                            ? 'bg-blue-500 border-blue-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>

                        {/* Avatar */}
                        <div className="flex-shrink-0 mr-3">
                          <Avatar user={user} size="sm" />
                        </div>

                        {/* User Info */}
                        <div className="flex-1 min-w-0 text-left">
                          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">
                            {user.display_name}
                            {user.is_bot && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                <Bot className="w-2.5 h-2.5" />
                                AI
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-dark-text-muted truncate">
                            {user.is_bot ? 'AI Асистент' : user.email}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border-primary flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-dark-text-muted">
            {selectedUserIds.length > 1
              ? `Групов чат с ${selectedUserIds.length} човека`
              : selectedUserIds.length === 1
              ? 'Лично съобщение'
              : 'Изберете потребител'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-dark-text-secondary hover:text-gray-800 dark:hover:text-dark-text-primary"
            >
              Отказ
            </button>
            {selectedUserIds.length > 0 && (
              <button
                onClick={handleStartDM}
                disabled={createDMThreadMutation.isPending}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {selectedUserIds.length > 1 ? 'Създай група' : 'Започни разговор'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartDMModal;
